/**
 * `useOrgIdentities(orgId)` / `useCohortIdentities(cohortId)` — **single
 * source** of people's identity on the client (D3, plan 06 · stages E2/E3).
 *
 * There used to be six identity joins scattered across the people,
 * schedule, and cohort screens, all resting on the `"user"` table that the
 * `organizations`, `my_cohorts_teacher` and `org_admin_scope` streams
 * replicated —with everyone's email— to any member. The problem was NOT the
 * transport: the REST that replaces it filtered exactly as badly until E2.
 * Now the server redacts email except for whoever has `ManageMembers` or
 * `ViewCampusData` (`org_rbac::can_view_member_emails`), and these hooks are
 * the only point through which identity enters the client.
 *
 * **Single path since E3's restrictive commit.** The
 * `academy.data.rest_identities` flag used to switch between REST and the
 * local mirror; with `"user"` out of the streams, the local table has ONE
 * row (its own) and there's nowhere to fall back to, so the rollback branch
 * and the flag disappear. The permission never hung off the flag: turning
 * it off never reopened the server-side leak.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  academyPeopleQueryKeys,
  cohortQueryKeys,
  type components,
  type OrgIdentity,
} from "@space/shared";
import { api, createApiQuery } from "@/lib/api";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

type MemberDTO = components["schemas"]["OrganizationMemberDTO"];
type RosterEntryDTO = components["schemas"]["CohortRosterEntryDTO"];

/** `userId → identity` map + loading signal (what the screens consume). */
export interface OrgIdentitiesResult {
  identities: Accessor<ReadonlyMap<string, OrgIdentity>>;
  isLoading: Accessor<boolean>;
  /** A specific person's identity (`undefined` while it hasn't arrived yet). */
  identityOf: (userId: string | undefined) => OrgIdentity | undefined;
}

/** Normalizes `listMembers`'s REST response into the shared shape. */
function fromMemberDTO(m: MemberDTO): OrgIdentity {
  return {
    userId: m.userId,
    role: m.role,
    campusId: m.campusId ?? null,
    name: m.user.name || null,
    // `null` = redacted by capability; the UI treats it as "no email".
    email: m.user.email ?? null,
    image: m.user.image ?? null,
  };
}

/** Normalizes a cohort roster entry into the shared shape. */
function fromRosterDTO(e: RosterEntryDTO): OrgIdentity {
  return {
    userId: e.userId,
    role: e.role ?? null,
    campusId: e.campusId ?? null,
    name: e.name ?? null,
    // `null` = redacted by capability, same as in `listMembers`.
    email: e.email ?? null,
    image: e.image ?? null,
  };
}

/** Packs a list of DTOs into the result the screens consume. */
function toResult<T>(
  rows: Accessor<T[] | undefined>,
  isLoading: Accessor<boolean>,
  map: (row: T) => OrgIdentity,
): OrgIdentitiesResult {
  const identities = createMemo<ReadonlyMap<string, OrgIdentity>>(() => {
    const out = new Map<string, OrgIdentity>();
    for (const row of rows() ?? []) {
      const identity = map(row);
      out.set(identity.userId, identity);
    }
    return out;
  });
  return {
    identities,
    isLoading,
    identityOf: (userId) => (userId ? identities().get(userId) : undefined),
  };
}

export function useOrgIdentities(orgId: Accessor<string | undefined>): OrgIdentitiesResult {
  const rest = createApiQuery<MemberDTO[], unknown>(
    () => academyPeopleQueryKeys.identities(orgId()),
    async () =>
      api.GET("/api/org/listMembers", {
        params: { query: { organizationId: orgId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(orgId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    }),
  );
  return toResult(
    () => rest.data,
    () => rest.isLoading,
    fromMemberDTO,
  );
}

/**
 * Identity of the people in ONE COHORT (enrolled students + assigned
 * teachers) — plan 06 · E3.
 *
 * It's the same hook as [`useOrgIdentities`] with a different scope, and
 * returns the same `Map<userId, OrgIdentity>`: the roster, the attendance
 * report and the alerts all hydrate from here, and no screen goes back to
 * joining with `"user"` on its own.
 *
 * Why it exists in addition to the org-scoped one: `GET
 * /api/cohorts/:id/roster` authorizes per cohort (teacher of THAT cohort,
 * campus_admin of THEIR campus, org_admin) and returns 20 people where
 * `listMembers` would return the academy's 2,000. Using the org-wide
 * endpoint to render a roster would be over-asking and authorizing on the
 * broadest scope.
 */
export function useCohortIdentities(cohortId: Accessor<string | undefined>): OrgIdentitiesResult {
  const rest = createApiQuery<RosterEntryDTO[], unknown>(
    () => cohortQueryKeys.identities(cohortId()),
    async () =>
      api.GET("/api/cohorts/{id}/roster", {
        params: { path: { id: cohortId() ?? "" } },
      }),
    () => ({
      enabled: Boolean(cohortId()),
      staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    }),
  );
  return toResult(
    () => rest.data,
    () => rest.isLoading,
    fromRosterDTO,
  );
}
